import React from 'react';
import { A3Seite } from './A3Seite';
import { Abschnitt, AbschnittLeer, AbschnittTabelle, AbschnittZeile, AbschnittZelle } from './Abschnitt';
import { HANDBUCH_FARBEN } from './stil';
import { formatDatum } from '../../../services/assignmentLogic';
import { koordinierungsSchluessel, wirksamerZeitraumDerFassung, zaehleBewertungen } from '../handbuchLogic';
import { berechneAlleUeberschneidungen } from '../../koordinierung/ueberschneidung';
import { baueZeitachse, paarKey } from '../../koordinierung/koordinierungLogic';
import type { GewerkAusPlan as KoordinierungsGewerk, KoordinierungPaar } from '../../koordinierung/koordinierungLogic';
import { UNBEKANNT } from '../../koordinierung/gewerkeKatalog';
import type { GewerkAusPlan, Handbuch, HandbuchGewerk } from '../types';

/**
 * Kapitel „Gesamtzeitplan" — alle Gewerke auf einer Zeitachse.
 *
 * Portiert aus `pages/editor/GesamtzeitplanPage.tsx` der Quell-App
 * (Monatsraster als Kopfzeile, ein Balken je Gewerk, Überschneidungszonen als
 * senkrechte Bänder, Zusammenfassungstabelle darunter). Zwei Dinge wandern
 * NICHT mit:
 *
 * 1. **`generateMonths` (dort :22-31) und `xPos` (:63-65) rechnen mit `Date`.**
 *    `new Date('2026-01-01')` ist Mitternacht UTC; in einer negativen Zeitzone
 *    liefert die Rückrechnung den 31.12. Dieselbe Falle steckt in
 *    `hazard-matrix.ts` der Quell-App. Hier rechnet `baueZeitachse`
 *    (`koordinierungLogic.ts`) rein lexikalisch und ohne `Date` — geprüfte
 *    Logik, die sich Koordinierungsmodul und Handbuch teilen. Zwei
 *    Zeitachsen-Implementierungen über dieselben Daten würden früher oder
 *    später verschiedene Bilder zeigen.
 * 2. **Die gleich breiten Monatsspalten.** Der Plotter-Generator verteilt die
 *    Monate gleichmäßig, während die Balken linear nach Tagen liegen; bei
 *    ungleich langen Monaten laufen Kopfzeile und Balken um bis zu anderthalb
 *    Tage auseinander. `baueZeitachse` macht die Spalten proportional zur
 *    echten Monatslänge.
 *
 * Die Seite SCHREIBT NICHTS. Sie liest die Gewerkeseiten des Handbuchs, die
 * Zeiträume der Planfassung und — nur für die Spalte „bewertet" — die
 * Bewertungen aus `koordinierung_paare`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// REINE FUNKTIONEN
//
// `wirksamerZeitraumDerFassung` und `zaehleBewertungen` liegen in
// `handbuchLogic.ts` —
// dort sind sie geprüft (es gibt kein jsdom, eine Komponente ist nicht
// testbar). Was hier bleibt, ist die Anpassung an `baueZeitachse` und reine
// Darstellung; nicht exportiert, weil
// `react-refresh/only-export-components` in einer Komponentendatei keine
// Wertexporte zulässt und `npm run lint` mit `--max-warnings 0` läuft.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Übersetzt eine Gewerkeseite in die Form, die `baueZeitachse` erwartet.
 *
 * Der Schlüssel bleibt `gewerk_norm` des Handbuchs und wird NICHT auf den
 * Katalogschlüssel der Koordinierung umgestellt: im Zeitplan soll jede
 * Gewerkeseite ihren eigenen Balken haben. „Elektro" und „Elektro NU 2" sind
 * zwei Firmen mit zwei Bauzeiten; die Koordinierung führt sie zu Recht
 * zusammen, ein Terminplan darf das nicht.
 *
 * Die Felder, die nur die Koordinierung braucht (`treffer`, `bereiche`,
 * `schritteOhneDatum` …), werden neutral gefüllt. Sie fließen in die
 * Zeitrechnung nicht ein — `baueZeitachse` liest ausschließlich `norm`,
 * `label`, `von` und `bis`.
 */
const alsZeitachsenGewerk = (
    gewerk: HandbuchGewerk,
    zeitraum: { von: string | null; bis: string | null },
): KoordinierungsGewerk => ({
    norm: gewerk.gewerk_norm,
    label: gewerk.gewerk_label,
    von: zeitraum.von,
    bis: zeitraum.bis,
    treffer: UNBEKANNT,
    zugeordnet: false,
    vonHandZugeordnet: false,
    schreibweisen: [gewerk.gewerk_label],
    schrittIds: [],
    anzahlSchritte: 0,
    schritteOhneDatum: 0,
    bereiche: [],
});

/**
 * Ab wie vielen Balken das A3-Blatt nicht mehr reicht.
 *
 * `A3Seite` schneidet ab (`overflow: hidden` — auf Papier gibt es keine
 * Bildlaufleiste). Ein abgeschnittener Terminplan im Handbuch ist schlimmer
 * als ein Hinweis darauf, dass er abgeschnitten wird.
 */
const BALKEN_HOECHSTZAHL = 24;

const ZEILEN_HOEHE = 15;

// ═══════════════════════════════════════════════════════════════════════════
// DARSTELLUNG
// ═══════════════════════════════════════════════════════════════════════════

const prozent = (anteil: number): string => `${(anteil * 100).toFixed(3)}%`;

interface Props {
    /** Kopfzeile der Seite, üblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fußzeile der Seite, üblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    /** Die Gewerkeseiten des Handbuchs. */
    gewerke: HandbuchGewerk[];
    /**
     * Der Handbuchsatz — er entscheidet, OB der Zeitraum noch aus dem Plan
     * gerechnet werden darf.
     *
     * Eine freigegebene Fassung liest den laufenden Plan nicht mehr: sonst
     * zeigte derselbe Druck derselben Fassung nach einer Terminverschiebung
     * andere Daten, ohne dass jemand etwas am Handbuch getan haette. Der
     * Entwurf liest weiter live — er SOLL zeigen, dass der Plan gewandert
     * ist. Die Unterscheidung trifft `wirksamerZeitraumDerFassung`.
     */
    handbuch?: Handbuch | null;
    /** Gewerke der Planfassung — liefern den Zeitraum, wo die Gewerkeseite
     *  keinen eigenen trägt. */
    planGewerke?: GewerkAusPlan[];
    /** Bewertungen aus `koordinierung_paare`, nur für die Spalte „bewertet". */
    paare?: KoordinierungPaar[];
    /** Projektlaufzeit aus `globalData.startDate/endDate`. Zieht die Achse auf,
     *  wenn sie über die Gewerke hinausreicht — ein Terminplan, der am ersten
     *  Gewerk beginnt, verschweigt den Vorlauf. */
    projektVon?: string | null;
    projektBis?: string | null;
}

export const Gesamtzeitplan: React.FC<Props> = ({
    kopf, fuss, gewerke, handbuch = null, planGewerke = [], paare = [], projektVon, projektBis,
}) => {
    const liste = [...(gewerke || [])].sort((a, b) => a.sortierung - b.sortierung);
    const ausPlan = new Map<string, GewerkAusPlan>((planGewerke || []).map(g => [g.gewerk_norm, g]));

    const alle = liste.map(g => alsZeitachsenGewerk(g, wirksamerZeitraumDerFassung(g, ausPlan, handbuch)));
    const mitZeitraum = alle.filter(g => g.von && g.bis && g.von <= g.bis);
    // Ohne Zeitraum heißt NICHT „fällt weg": diese Gewerke stehen unten
    // namentlich. Sie stillschweigend zu übergehen wäre der gefährlichere
    // Fehler — der Zeitplan sähe vollständig aus.
    const ohneZeitraum = alle.filter(g => !(g.von && g.bis && g.von <= g.bis));

    const ueberschneidungen = berechneAlleUeberschneidungen(mitZeitraum);
    const achse = baueZeitachse(mitZeitraum, ueberschneidungen, projektVon, projektBis);
    const bewertungen = zaehleBewertungen(paare || []);

    return (
        <A3Seite kopf={kopf} fuss={fuss}>
            <div style={{ marginBottom: '6px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>
                    GESAMTZEITPLAN — ALLE GEWERKE
                </div>
                <div style={{ fontSize: '8px', color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}>
                    {/* Der Satz zur Freigabe steht wortgleich im Generator
                        (`generatorHandbuch.ts`, Kapitel Gesamtzeitplan) und
                        ohne Fallunterscheidung — er ist in beiden Zuständen
                        wahr. */}
                    Zeiträume aus dem SiGe-Plan (Arbeitsschritte je Gewerk); ein am Handbuch
                    gesetzter Zeitraum geht vor. Eine freigegebene Fassung zeigt die bei der
                    Freigabe festgehaltenen Zeiträume. Rot schraffiert: Zeitfenster, in denen
                    dieses Gewerk mit mindestens einem anderen zusammentrifft.
                </div>
            </div>

            {liste.length > BALKEN_HOECHSTZAHL && (
                <div
                    style={{
                        fontSize: '8px',
                        color: HANDBUCH_FARBEN.risikoMittel,
                        marginBottom: '4px',
                        fontStyle: 'italic',
                    }}
                >
                    {liste.length} Gewerke: ab {BALKEN_HOECHSTZAHL + 1} Balken schneidet das A3-Blatt
                    ab. Gewerkeseiten zusammenführen oder den Zeitplan der Koordinierung beilegen.
                </div>
            )}

            <div className="flex-1 min-h-0 flex flex-col gap-[6px]">
                <Abschnitt
                    titel="Zeitachse"
                    zusatz={achse ? `${formatDatum(achse.start)} – ${formatDatum(achse.ende)}` : undefined}
                    className="shrink-0"
                >
                    {!achse ? (
                        <AbschnittLeer text="Kein Gewerk trägt einen verwertbaren Zeitraum — es lässt sich keine Zeitachse aufziehen." />
                    ) : (
                        <div>
                            {/* Monatskopf. Die Spaltenbreite folgt der echten
                                Monatslänge, damit der Balkenanfang unter dem
                                richtigen Monat sitzt. */}
                            <div
                                className="relative"
                                style={{
                                    marginLeft: '120px',
                                    height: '13px',
                                    borderBottom: `1px solid ${HANDBUCH_FARBEN.linie}`,
                                }}
                            >
                                {achse.monate.map(m => (
                                    <div
                                        key={`${m.jahr}-${m.monat}`}
                                        className="absolute top-0 bottom-0 flex items-center justify-center overflow-hidden"
                                        style={{
                                            left: prozent(m.anteilVon),
                                            width: prozent(m.anteilBis - m.anteilVon),
                                            borderLeft: `1px solid ${HANDBUCH_FARBEN.linie}`,
                                            fontSize: '7px',
                                            color: HANDBUCH_FARBEN.textLeise,
                                        }}
                                    >
                                        {m.label}
                                    </div>
                                ))}
                            </div>

                            {achse.balken.map(b => (
                                <div
                                    key={b.norm}
                                    className="flex items-center"
                                    style={{ height: `${ZEILEN_HOEHE}px` }}
                                >
                                    <div
                                        className="truncate shrink-0"
                                        style={{ width: '120px', paddingRight: '4px', fontSize: '8px', fontWeight: 600 }}
                                        title={b.label}
                                    >
                                        {b.label}
                                    </div>
                                    <div className="relative flex-1" style={{ height: `${ZEILEN_HOEHE}px` }}>
                                        {/* Monatsgitter, damit sich ein Balken
                                            ohne Lineal ablesen lässt. */}
                                        {achse.monate.map(m => (
                                            <div
                                                key={`${b.norm}-${m.jahr}-${m.monat}`}
                                                className="absolute top-0 bottom-0"
                                                style={{ left: prozent(m.anteilVon), borderLeft: '1px solid #ededed' }}
                                            />
                                        ))}
                                        {b.zonen.map(z => (
                                            <div
                                                key={`${b.norm}-zone-${z.von}`}
                                                className="absolute top-0 bottom-0"
                                                style={{
                                                    left: prozent(z.anteilVon),
                                                    width: prozent(z.anteilBis - z.anteilVon),
                                                    background: 'rgba(185,28,28,0.10)',
                                                    borderLeft: `1px dashed ${HANDBUCH_FARBEN.risikoHoch}`,
                                                    borderRight: `1px dashed ${HANDBUCH_FARBEN.risikoHoch}`,
                                                }}
                                            />
                                        ))}
                                        <div
                                            className="absolute"
                                            style={{
                                                left: prozent(b.anteilVon),
                                                width: prozent(b.anteilBis - b.anteilVon),
                                                top: '3px',
                                                height: '9px',
                                                minWidth: '2px',
                                                background: HANDBUCH_FARBEN.linieStark,
                                                borderRadius: '1px',
                                            }}
                                            title={`${b.label}: ${formatDatum(b.von)} – ${formatDatum(b.bis)}`}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Abschnitt>

                <Abschnitt
                    titel="Überschneidungen — Zusammenfassung"
                    zusatz={`${ueberschneidungen.length} Paar(e)`}
                    className="flex-1"
                >
                    {ueberschneidungen.length === 0 ? (
                        /* „Keine Überschneidung" ist hier eine echte Feststellung
                           und kein Datenmangel — aber nur, wenn überhaupt
                           gerechnet werden konnte. */
                        <AbschnittLeer
                            text={mitZeitraum.length < 2
                                ? 'Für einen Vergleich werden mindestens zwei Gewerke mit Zeitraum gebraucht — es wurde nichts geprüft.'
                                : 'Keine zeitliche Überschneidung zwischen den Gewerken festgestellt.'}
                        />
                    ) : (
                        <AbschnittTabelle
                            kopf={['Gewerk A', 'Gewerk B', 'Gemeinsamer Zeitraum', 'Tage', 'Bewertet']}
                            breiten={['24%', '24%', '26%', '8%', '18%']}
                        >
                            {ueberschneidungen.map((u, i) => {
                                // Die Bewertung hängt am Katalogschlüssel der
                                // Koordinierung, der Balken am Handbuch-Schlüssel.
                                // Umgerechnet wird über `koordinierungsSchluessel`
                                // — dieselbe Brücke wie in der Gefährdungsmatrix
                                // und dort geprüft. `aNorm`/`bNorm` sind hier die
                                // `gewerk_norm` der Handbuchseiten.
                                const key = paarKey(
                                    koordinierungsSchluessel({ gewerk_norm: u.aNorm, gewerk_label: u.aLabel }),
                                    koordinierungsSchluessel({ gewerk_norm: u.bNorm, gewerk_label: u.bLabel }),
                                );
                                const zahl = bewertungen.get(key);
                                return (
                                    <AbschnittZeile key={`${u.aNorm}~${u.bNorm}`} erste={i === 0}>
                                        <AbschnittZelle>{u.aLabel}</AbschnittZelle>
                                        <AbschnittZelle>{u.bLabel}</AbschnittZelle>
                                        <AbschnittZelle>
                                            {formatDatum(u.von)} – {formatDatum(u.bis)}
                                        </AbschnittZelle>
                                        <AbschnittZelle
                                            style={{
                                                fontWeight: 700,
                                                color: u.ampel === 'rot'
                                                    ? HANDBUCH_FARBEN.risikoHoch
                                                    : HANDBUCH_FARBEN.risikoMittel,
                                            }}
                                        >
                                            {u.dauerTage}
                                        </AbschnittZelle>
                                        <AbschnittZelle>
                                            {!zahl ? (
                                                <span style={{ color: HANDBUCH_FARBEN.risikoHoch, fontWeight: 700 }}>
                                                    ✗ offen
                                                </span>
                                            ) : (
                                                <span>
                                                    {zahl.bestaetigt > 0 && (
                                                        <span style={{ color: HANDBUCH_FARBEN.statusOk, fontWeight: 700 }}>
                                                            ✓ {zahl.bestaetigt}
                                                        </span>
                                                    )}
                                                    {zahl.offen > 0 && (
                                                        <span style={{ color: HANDBUCH_FARBEN.risikoMittel }}>
                                                            {zahl.bestaetigt > 0 ? ' · ' : ''}
                                                            {zahl.offen} unbestätigt
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </AbschnittZelle>
                                    </AbschnittZeile>
                                );
                            })}
                        </AbschnittTabelle>
                    )}
                </Abschnitt>

                {/* Der Ausschuss ist Teil des Ergebnisses, nicht sein Abfall. */}
                {ohneZeitraum.length > 0 && (
                    <Abschnitt
                        titel="Gewerke ohne verwertbaren Zeitraum"
                        zusatz={`${ohneZeitraum.length}`}
                        className="shrink-0"
                    >
                        <div style={{ fontSize: '8.5px' }}>
                            <div style={{ color: HANDBUCH_FARBEN.risikoHoch, fontWeight: 700, marginBottom: '2px' }}>
                                Diese Gewerke sind im Zeitplan NICHT dargestellt und in keiner
                                Überschneidung geprüft:
                            </div>
                            {ohneZeitraum.map(g => g.label).join(' · ')}
                            <div style={{ color: HANDBUCH_FARBEN.textLeise, marginTop: '2px' }}>
                                Zeitraum im SiGe-Plan am Arbeitsschritt nachtragen oder am Gewerk des
                                Handbuchs setzen.
                            </div>
                        </div>
                    </Abschnitt>
                )}
            </div>

            <div
                style={{
                    borderTop: `1px solid ${HANDBUCH_FARBEN.linie}`,
                    marginTop: '4px',
                    paddingTop: '3px',
                    fontSize: '7.5px',
                    color: HANDBUCH_FARBEN.textLeise,
                }}
            >
                Spalte „Bewertet": ✓ = im Modul „Gewerke-Überschneidung" beurteilt und bestätigt.
                „✗ offen" heißt, dass die Gewerke zeitgleich arbeiten, ohne dass eine gegenseitige
                Gefährdung beurteilt wurde — es heißt nicht, dass keine besteht.
            </div>
        </A3Seite>
    );
};
